import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import NewCommentInput from './audio-comments/new-comment-input';
import DeleteCommentButton from './audio-comments/delete-comment-button';
import LikeCommentButton from './audio-comments/like-comment-button';

const AudioComments = (props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [commentsCount, setCommentsCount] = useState(props.commentsCount);
  const [liked, setLiked] = useState(props.liked);
  const [likesCount, setLikesCount] = useState(props.likesCount);

  const loadComments = async ({
    comment_id = undefined,
  } = {}) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`/ajax/audio/${props.audioId}/comments`);
      const responseComments = Array.isArray(response.data) ? response.data : [];
      /* If comment_id is provided, alter only that comment to avoid reordering.
       * The comment_id must not be a nested one. */
      let commentIndex = -1;
      let responseCommentIndex = -1;
      if (comment_id !== undefined) {
        commentIndex = comments.findIndex((comment) => comment.comment_id === comment_id);
        responseCommentIndex = responseComments.findIndex((comment) => comment.comment_id === comment_id);
      }
      if (commentIndex !== -1 && responseCommentIndex !== -1) {
        /* substitute the comment index with the response comment index */
        const newComments = [...comments];
        newComments[commentIndex] = responseComments[responseCommentIndex];
        setComments(newComments);
      } else {
        setComments(responseComments);
      }
      setCommentsCount(getComentsCountFromComments(responseComments));
    } catch {
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadLikeStatus = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`/ajax/audio/${props.audioId}/like-status`);
      setLiked(response.data.liked);
      setLikesCount(response.data.likes_count);
    } catch {
      // do nothing
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    loadComments();
  }, [isExpanded, props.audioId]);

  const toggleExpandedHandler = () => {
    if (isLoading) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const getComentsCountFromComments = (comments) => {
    let count = 0;
    for (const comment of comments) {
      if (!comment.is_deleted) {
        count++;
      }
      count += comment.children.length;
    }
    return count;
  }

  const toggleAudioLike = async () => {
    if (!props.canLike || isLoading) {
      return;
    }
    setIsLoading(true);
    const url = `/ajax/audio/${props.audioId}/like`;
    let response;
    if (liked) {
      response = await axios.delete(url);
    } else {
      response = await axios.post(url);
    }
    loadLikeStatus();
  }

  const renderComment = (comment, keyPrefix, canComment, parentComment) => {
    return (
      <div className="comment-wrapper d-flex justify-content-between align-items-start">
        <div className="comment-avatar flex-shrink-0">
          <img className="img-fluid rounded-circle mx-auto" src={comment.profile_picture_url} width="64" height="64" alt="" />
        </div>
        <div className="comment-content-wrapper flex-grow-1 ms-2">
          {comment.is_deleted ? (
            <div className="content-header">
              <div className="opacity-50">This comment was deleted</div>
            </div>
          ) : (
            <>
              <div className="content-header">
                <div><strong>{comment.artistic_name}</strong></div>
                <div><small>{comment.created_at_datetime_short}</small></div>
              </div>
              <div style={{ whiteSpace: 'pre-line' }}>{comment.content}</div>
              <div className="content-footer d-flex align-items-center gap-4">
                <LikeCommentButton
                  audioId={props.audioId}
                  commentId={comment.comment_id}
                  canLike={comment.can_like}
                  liked={comment.liked}
                  likesCount={comment.likes_count}
                  likeStatusChangedHandler={() => loadComments({
                      /* We pass the corresponding not nested comment to tell
                      * only that comment has to be updated so no reordering
                      * happens. */
                      comment_id: parentComment ? parentComment.comment_id : comment.comment_id
                    })}
                />
                <span></span>
                {comment.can_delete ? (
                  <DeleteCommentButton
                    audioId={props.audioId}
                    commentId={comment.comment_id}
                    commentDeletedHandler={loadComments}
                  />
                ) : null}
              </div>
            </>
          )}
          {canComment ? (
            <NewCommentInput
              audioId={props.audioId}
              commentId={comment.comment_id}
              newCommentSubmitHandler={loadComments}
              initiallyCollapsed={true}
              loggedProfilePictureUrl={props.loggedProfilePictureUrl}
              placeholder={`Write a reply`}
            />
          ) : null}
          {comment.children.length ? (
            <ul className="audio-comments-list audio-comments-list-2">
              {comment.children.map((childComment, childIndex) => (
                <li key={`${keyPrefix}-child-${childIndex}`} className="mb-2">
                  {renderComment(childComment, `${keyPrefix}-child-${childIndex}`, false, comment)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    );
  };

  const renderCommentsButtonAndCount = () => {
    return props.commentsEnabled ? (
      <div className="d-inline-flex align-items-center">
        <button
          type="button"
          className="p-0 btn btn-link text-body text-decoration-none d-inline-block"
          onClick={props.canComment || commentsCount > 0 ? toggleExpandedHandler : undefined}
          >
          <span className="icon-comment-o fs-4 me-2"></span>
        </button>
        <span>{commentsCount}</span>
      </div>
    ) : (
      <div className="opacity-50 text-decoration-none d-inline-flex align-items-center">
        <span className="icon-comment-o fs-4 me-2"></span> -
      </div>
    )
  }

  const renderLikeButtonAndCount = () => {
    return (
      <div className="d-inline-flex align-items-center ms-3">
        <button
          type="button"
          className={`p-0 btn btn-link text-body text-decoration-none d-inline-block ms-2 me-2 ${props.canLike ? '' : 'opacity-50 disabled'}`}
          onClick={toggleAudioLike}
          ><i className={`icon-heart${liked ? '' : '-o'} color-heart fs-4`}></i></button>
        <span className="d-inline-block">{likesCount}</span>
      </div>
    )
  }

  return (
    <div className={`${isLoading ? 'opacity-50' : ''}`} data-audio-id={props.audioId}>
      <div class="row pb-2">
        <div class="col-4">
          {renderCommentsButtonAndCount()}
          {renderLikeButtonAndCount()}
        </div>
        <div class="col-8 d-flex justify-content-end gap-2">
          {props.playCountString ? (
            <span class="metatadata-info-box"><span class="metadata-info-label">PLAYED:</span> <span class="metadata-info-value">{props.playCountString}</span></span>
          ) : null}
          {props.createdAtDateShort ? (
            <span class="metatadata-info-box"><span class="metadata-info-label">POSTED:</span> <span class="metadata-info-value">{props.createdAtDateShort}{props.createdAtTime ? ` ${props.createdAtTime}` : ''}</span></span>
          ) : null}
        </div>
      </div>
      {isExpanded ? (
        <>
          {props.canComment ? (
            <NewCommentInput
              audioId={props.audioId}
              commentId={null}
              newCommentSubmitHandler={loadComments}
              initiallyCollapsed={false}
              loggedProfilePictureUrl={props.loggedProfilePictureUrl}
            />
          ) : null}
          <ul id={`audio-comments-list-${props.audioId}`} className="audio-comments-list audio-comments-list-1">
            {comments.map((comment, index) => (
              <li key={`${props.audioId}-${index}`} className="mb-2">
                {renderComment(comment, `${props.audioId}-${index}`, props.canComment, undefined)}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  )
};

AudioComments.propTypes = {
  commentsEnabled: PropTypes.bool,
  audioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  commentsCount: PropTypes.number.isRequired,
  canComment: PropTypes.bool.isRequired,
  liked: PropTypes.bool.isRequired,
  likesCount: PropTypes.number.isRequired,
  canLike: PropTypes.bool.isRequired,
  loggedProfilePictureUrl: PropTypes.string.isRequired,
  playCountString: PropTypes.string,
  createdAtDateShort: PropTypes.string,
  createdAtTime: PropTypes.string,
};


export default AudioComments;
